import React from 'react';
import { useQuery } from '@tanstack/react-query';

import type { UseQueryWrapper, DeletedConnection } from 'app/types';
import { useSnakeBar } from '../../useSnakeBar';
import type { GetDeletedConnectionsArgs } from '../../../Domain/Admin/Services';
import { adminServices } from '../../../Domain/Admin/Services';

type GetDeletedConnectionsQueryVariables = GetDeletedConnectionsArgs;

type GetDeletedConnectionsQueryResultData = {
  data: DeletedConnection[];
  totalItems: number;
};

const queryFn = (variables: GetDeletedConnectionsQueryVariables) => async () => {
  const response = await adminServices.getDeletedConnections(variables);
  return response.data;
};

const QUERY_KEY = 'getDeletedConnections';

export const useDeletedConnectionsQuery: UseQueryWrapper<
  GetDeletedConnectionsQueryResultData,
  GetDeletedConnectionsQueryVariables
> = ({ variables, options }) => {
  const { showErrorSnakeBar } = useSnakeBar();

  const onError = React.useCallback(() => {
    showErrorSnakeBar({ method: QUERY_KEY });
  }, [showErrorSnakeBar]);

  const select = React.useCallback((data: any) => {
    return {
      data: data['hydra:member'] || [],
      totalItems: data['hydra:totalItems'] || 0,
    };
  }, []);

  return useQuery<GetDeletedConnectionsQueryResultData>(
    [QUERY_KEY, ...Object.values(variables || {})],
    queryFn(variables),
    {
      select,
      onError,
      enabled: !!variables.employerId,
      ...(options || {}),
    },
  );
};
